// src/components/CustomersAnalysis.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import EmailEditor from './EmailEditor';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || "http://localhost:3004";

const CustomersAnalysis = ({ token }) => {
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('totalSpent');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  
  // Email functionality states
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        search: debouncedSearchTerm,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        sortBy
      };

      const response = await axios.get(`${API_URL}/customers/analysis`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      });

      setCustomers(response.data.customers);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching customers analysis:', err);
      if (err.response?.status !== 401) {
        setError('Nie udało się pobrać danych klientów.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, debouncedSearchTerm, categoryFilter, sortBy]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/customers/stats`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching customer stats:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (currentPage === 1 && !debouncedSearchTerm && categoryFilter === 'all') {
      fetchStats();
    }
  }, [fetchStats, currentPage, debouncedSearchTerm, categoryFilter]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryFilter = (category) => {
    setCategoryFilter(category);
    setCurrentPage(1);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
  };

  // Email functions
  const handleSelectCustomer = (customerEmail) => {
    const newSelected = new Set(selectedCustomers);
    if (newSelected.has(customerEmail)) {
      newSelected.delete(customerEmail);
    } else {
      newSelected.add(customerEmail);
    }
    setSelectedCustomers(newSelected);
  };

  const handleSelectAllVisible = () => {
    const newSelected = new Set(selectedCustomers);
    customers.forEach(customer => {
      newSelected.add(customer.email);
    });
    setSelectedCustomers(newSelected);
  };

  const handleSelectAllCategory = (category) => {
    const newSelected = new Set(selectedCustomers);
    customers.forEach(customer => {
      if (getCustomerCategory(customer) === category) {
        newSelected.add(customer.email);
      }
    });
    setSelectedCustomers(newSelected);
  };

  const handleDeselectAll = () => {
    setSelectedCustomers(new Set());
  };

  const openEmailEditor = () => {
    if (selectedCustomers.size === 0) {
      setError('Wybierz przynajmniej jednego klienta do wysłania emaila');
      return;
    }
    setShowEmailEditor(true);
    setError('');
  };

  const closeEmailEditor = () => {
    setShowEmailEditor(false);
  };

  const sendBulkEmail = async (emailData) => {
    try {
      setSendingEmail(true);
      
      const response = await axios.post(`${API_URL}/customers/send-bulk-email`, {
        recipients: Array.from(selectedCustomers),
        subject: emailData.subject,
        htmlContent: emailData.htmlContent,
        textContent: emailData.textContent
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setError('');
        alert(`Email został wysłany do ${selectedCustomers.size} klientów!`);
        closeEmailEditor();
        setSelectedCustomers(new Set());
      }
    } catch (err) {
      console.error('Error sending bulk email:', err);
      setError('Nie udało się wysłać emaila. Spróbuj ponownie.');
    } finally {
      setSendingEmail(false);
    }
  };

  const getCustomerCategory = (customer) => {
    if (customer.totalOrders >= 10) return 'vip';
    if (customer.totalOrders >= 5) return 'loyal';
    if (customer.totalOrders >= 2) return 'regular';
    return 'new';
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'vip': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'loyal': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'regular': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'new': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryText = (category) => {
    switch (category) {
      case 'vip': return 'VIP (10+ zakupów)';
      case 'loyal': return 'Stały (5-9 zakupów)';
      case 'regular': return 'Regularny (2-4 zakupy)';
      case 'new': return 'Nowy (1 zakup)';
      default: return category;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  const calculateDaysSinceLastOrder = (lastOrderDate) => {
    const today = new Date();
    const lastOrder = new Date(lastOrderDate);
    const diffTime = Math.abs(today - lastOrder);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading && customers.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-center">Ładowanie analizy klientów...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="pt-16 pb-8">
          
          {/* Header */}
          <div className="text-center mb-8">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Analiza Klientów</h1>
            <p className="text-gray-600">Przegląd stałych klientów i statystyki zakupów</p>
          </div>

          {/* Stats Overview */}
          {stats && Object.keys(stats).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Wszyscy klienci</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalCustomers || 0}</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Klienci VIP</p>
                    <p className="text-2xl font-bold text-purple-600">{stats.vipCustomers || 0}</p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-full">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Średnia wartość</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.averageOrderValue || 0)}</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Całkowity obrót</p>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.totalRevenue || 0)}</p>
                  </div>
                  <div className="bg-orange-100 p-3 rounded-full">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters and Search */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Szukaj po imieniu, nazwisku lub emailu..."
                    value={searchTerm}
                    onChange={handleSearch}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: 'all', label: 'Wszystkie', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
                  { value: 'vip', label: 'VIP', color: 'bg-purple-100 text-purple-700 hover:bg-purple-200' },
                  { value: 'loyal', label: 'Stali', color: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' },
                  { value: 'regular', label: 'Regularni', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
                  { value: 'new', label: 'Nowi', color: 'bg-green-100 text-green-700 hover:bg-green-200' }
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => handleCategoryFilter(filter.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      categoryFilter === filter.value 
                        ? filter.color.replace('hover:', '').replace('100', '200')
                        : filter.color
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Sort */}
              <div className="min-w-0 sm:min-w-[200px]">
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="totalSpent">Kwota wydana ↓</option>
                  <option value="totalOrders">Liczba zamówień ↓</option>
                  <option value="lastOrderDate">Ostatnie zamówienie ↓</option>
                  <option value="firstOrderDate">Pierwsze zamówienie ↓</option>
                  <option value="name">Nazwisko A-Z</option>
                </select>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Results Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              Lista klientów {pagination.totalCount && `(${pagination.totalCount})`}
              {debouncedSearchTerm && ` (filtrowane)`}
            </h2>
            
            {/* Selection Controls */}
            <div className="flex items-center gap-4">
              {selectedCustomers.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Zaznaczono: {selectedCustomers.size}
                  </span>
                  <button
                    onClick={handleDeselectAll}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Odznacz wszystkie
                  </button>
                </div>
              )}
              
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAllVisible}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                >
                  Zaznacz widoczne
                </button>
                
                {categoryFilter !== 'all' && (
                  <button
                    onClick={() => handleSelectAllCategory(categoryFilter)}
                    className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                  >
                    Zaznacz kategorię
                  </button>
                )}
                
                {selectedCustomers.size > 0 && (
                  <button
                    onClick={openEmailEditor}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Wyślij Email ({selectedCustomers.size})
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Customers List */}
          {customers.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Brak klientów</h3>
              <p className="text-gray-600">
                {debouncedSearchTerm || categoryFilter !== 'all' ? 'Brak klientów pasujących do kryteriów' : 'Brak klientów w bazie danych'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {customers.map((customer) => {
                const category = getCustomerCategory(customer);
                const daysSinceLastOrder = calculateDaysSinceLastOrder(customer.lastOrderDate);
                
                return (
                  <div key={customer.email} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow relative">
                    {/* Selection Checkbox */}
                    <div className="absolute top-4 left-4 z-10">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.has(customer.email)}
                        onChange={() => handleSelectCustomer(customer.email)}
                        className="w-5 h-5 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
                      />
                    </div>
                    
                    <div className="p-6 pl-12">
                      {/* Customer Category Badge */}
                      <div className="flex items-center justify-between mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getCategoryColor(category)}`}>
                          {getCategoryText(category)}
                        </span>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">ID: {customer.customerId}</p>
                        </div>
                      </div>

                      {/* Customer Info */}
                      <div className="space-y-3 mb-4">
                        <div>
                          <h3 className="font-semibold text-gray-800 text-lg">{customer.fullName}</h3>
                          <p className="text-gray-600 text-sm break-all">{customer.email}</p>
                          {customer.phone && (
                            <p className="text-gray-600 text-sm">{customer.phone}</p>
                          )}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Zamówienia</p>
                          <p className="font-bold text-lg text-gray-800">{customer.totalOrders}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Wydane</p>
                          <p className="font-bold text-lg text-green-600">{formatCurrency(customer.totalSpent)}</p>
                        </div>
                      </div>

                      {/* Dates */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Pierwszy zakup:</span>
                          <span className="font-medium">{formatDate(customer.firstOrderDate)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Ostatni zakup:</span>
                          <span className="font-medium">{formatDate(customer.lastOrderDate)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Aktywność:</span>
                          <span className={`font-medium ${daysSinceLastOrder <= 30 ? 'text-green-600' : daysSinceLastOrder <= 90 ? 'text-orange-600' : 'text-red-600'}`}>
                            {daysSinceLastOrder === 0 ? 'Dziś' : `${daysSinceLastOrder} dni temu`}
                          </span>
                        </div>
                      </div>

                      {/* Average Order Value */}
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex justify-between">
                          <span className="text-gray-500 text-sm">Średnia wartość zamówienia:</span>
                          <span className="font-medium text-sm">{formatCurrency(customer.totalSpent / customer.totalOrders)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Poprzednia
                </button>
                
                <span className="px-4 py-2 text-sm text-gray-600">
                  Strona {currentPage} z {pagination.totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage === pagination.totalPages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Następna
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email Editor Modal */}
      <EmailEditor
        isOpen={showEmailEditor}
        onClose={closeEmailEditor}
        onSend={sendBulkEmail}
        selectedCount={selectedCustomers.size}
        loading={sendingEmail}
      />
    </div>
  );
};

export default CustomersAnalysis;
